import { apply, burst, celebrate, give, toast } from '../core/effects';
import { bus } from '../core/bus';
import { hasGame, S } from '../core/game';
import { WATER_MAX, type Crop, type GameState, type GardenPlot } from '../core/state';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { say } from '../ui/dialog';
import { logJournal, practice } from './journal';
import { rainedToday, weatherNow } from './weather';

/** Balcony garden: nine plots, three crops, one watering per plot per day, ripe after 3 days. */
export const CROPS: readonly Crop[] = ['strawberry', 'tomato', 'sunflower'];
export const SEED_COST: Record<Crop, number> = { strawberry: 5, tomato: 3, sunflower: 0 };
/** What a ripe plot gives. */
export const HARVEST: Record<Crop, { item: 'strawberries' | 'tomato' | 'sunflower'; n: number }> = {
  strawberry: { item: 'strawberries', n: 3 },
  tomato: { item: 'tomato', n: 2 },
  sunflower: { item: 'sunflower', n: 1 },
};
export const RIPE_AT = 3;
export const CAN_SIZE = 9;

/** Absolute game day (dayIndex never resets). */
const today = (s: GameState) => s.dayIndex;

/** 0 empty · 1 seedling · 2 sprout · 3 growing · 4 ripe. */
export function stage(p: GardenPlot): number {
  if (!p.crop) return 0;
  return Math.min(4, 1 + p.water + (p.water >= RIPE_AT ? 1 : 0));
}

export function wateredToday(p: GardenPlot, s: GameState = S()): boolean {
  return p.lastDay === today(s);
}

export function plant(i: number, crop: Crop, s: GameState = S()): boolean {
  const p = s.garden[i];
  if (!p || p.crop) return false;
  s.garden[i] = { crop, water: 0, lastDay: -1 };
  return true;
}

/** Water a plot: once a day, only while it is still growing. */
export function water(i: number, s: GameState = S()): boolean {
  const p = s.garden[i];
  if (!p?.crop || p.water >= RIPE_AT || wateredToday(p, s)) return false;
  p.water++;
  p.lastDay = today(s);
  return true;
}

/** Pick a ripe plot and empty it. */
export function harvest(i: number, s: GameState = S()): Crop | null {
  const p = s.garden[i];
  if (!p?.crop || p.water < RIPE_AT) return null;
  const crop = p.crop;
  s.garden[i] = { crop: null, water: 0, lastDay: -1 };
  return crop;
}

/** Rain waters every growing plot that wasn't watered today; returns how many. */
export function rainWater(s: GameState = S()): number {
  let n = 0;
  s.garden.forEach((_, i) => water(i, s) && n++);
  return n;
}

// --- Typhoon protection (the sky package's checklist calls protectPlants) -------------

const IN_KEY = 'fam.plantsIn.';
const RISK_KEY = 'fam.typhoonRisk.';

/** The plants were brought in today (protection also covers the night after). */
export function protectPlants(s: GameState = S()): void {
  s.daily['fam.plantsIn'] = true;
  s.forever[IN_KEY + today(s)] = true;
  bus.emit('change');
}
export function arePlantsProtected(s: GameState = S()): boolean {
  const d = today(s);
  return !!(s.daily['fam.plantsIn'] || s.forever[IN_KEY + d] || s.forever[IN_KEY + (d - 1)]);
}

/** Flatten every plant: back to seedlings. */
export function typhoonDamage(s: GameState = S()): number {
  let n = 0;
  for (const p of s.garden) {
    if (!p.crop || !p.water) continue;
    p.water = 0;
    p.lastDay = -1;
    n++;
  }
  return n;
}

/**
 * Day start: settle last night's typhoon (unprotected plants are flattened), then
 * warn if a typhoon comes today. Old bookkeeping keys are dropped.
 */
export function gardenDayStart(s: GameState = S()): void {
  const d = today(s);
  for (const k of Object.keys(s.forever)) {
    if (!k.startsWith(RISK_KEY)) continue;
    const day = Number(k.slice(RISK_KEY.length));
    if (day >= d) continue;
    delete s.forever[k];
    const safe = s.forever[IN_KEY + day] || s.forever[IN_KEY + (day - 1)];
    if (!safe && typhoonDamage(s)) toast(m().family.typhoonHit);
  }
  for (const k of Object.keys(s.forever)) if (k.startsWith(IN_KEY) && Number(k.slice(IN_KEY.length)) < d - 1) delete s.forever[k];
  if (weatherNow().kind === 'typhoon') {
    if (arePlantsProtected(s)) toast(m().family.plantsSafe);
    else {
      s.forever[RISK_KEY + d] = true;
      if (s.garden.some((p) => p.crop)) toast(m().family.typhoonWarn);
    }
  }
}

/** Whenever it has rained today, credit the watering once. */
export function gardenRainCheck(): void {
  if (!hasGame()) return;
  const s = S();
  if (s.daily['fam.rainCredit'] || !rainedToday()) return;
  s.daily['fam.rainCredit'] = true;
  if (rainWater(s)) toast(m().family.rainWatered);
}

// --- Interaction -------------------------------------------------------------------------

export function canLeft(s: GameState = S()): number {
  return s.counters['fam.can'] ?? 0;
}

function cropName(c: Crop): string {
  return m().family.crops[c].name;
}

export function usePlot(i: number): void {
  const s = S();
  const t = m().family;
  gardenRainCheck();
  const p = s.garden[i];
  const who = t.plotTitle(i + 1);
  if (arePlantsProtected(s) && weatherNow().kind === 'typhoon') return toast(t.covered);
  if (!p.crop) {
    return say({
      who,
      icon: '🪴',
      text: t.emptyText,
      choices: [
        ...CROPS.map((c) => ({
          text: `${t.plant(`${t.crops[c].icon} ${t.crops[c].seed}`)} · ${SEED_COST[c] ? `🪙 ${SEED_COST[c]}` : t.free}`,
          run: () => {
            if (s.coins < SEED_COST[c]) {
              sfx('bad');
              return toast(t.noCoins);
            }
            if (SEED_COST[c]) apply({ coins: -SEED_COST[c] }, { float: false });
            plant(i, c, s);
            sfx('rustle');
            burst('sparkle', 5);
            toast(t.planted(cropName(c)));
            practice('nature');
            bus.emit('change');
          },
        })),
        { text: t.notNow },
      ],
    });
  }
  if (p.water >= RIPE_AT) {
    const crop = harvest(i, s)!;
    const h = HARVEST[crop];
    give(h.item, h.n);
    celebrate(true);
    sfx('pickup');
    toast(t.harvest(`${t.crops[crop].icon}×${h.n}`));
    logJournal(t.crops[crop].icon, 'family.journal.harvest', cropName(crop));
    practice('nature', 2);
    return;
  }
  if (wateredToday(p, s)) return toast(`${t.growText(cropName(p.crop), t.stages[stage(p)], p.water)} — ${t.alreadyWatered}`);
  if (canLeft(s) <= 0) {
    sfx('bad');
    return toast(t.canEmpty);
  }
  s.counters['fam.can'] = canLeft(s) - 1;
  water(i, s);
  sfx('water');
  burst('drop', 8);
  apply({ mood: 1, minutes: 2 }, { float: false });
  toast(`${t.watered(p.water)} · ${t.canState(canLeft(s))}`);
}

export function useTap(): void {
  const s = S();
  const t = m().family;
  say({
    who: t.tapTitle,
    icon: '🚰',
    text: `${t.tapText}\n${t.canState(canLeft(s))}`,
    choices: [
      {
        text: t.fillCan(CAN_SIZE),
        run: () => {
          s.counters['fam.can'] = CAN_SIZE;
          sfx('water');
          burst('drop', 6);
          apply({ minutes: 3 }, { float: false });
          toast(t.canFilled);
        },
      },
      {
        text: t.fillBottle,
        run: () => {
          const need = WATER_MAX - s.bag.water;
          if (need > 0) give('water', need);
          sfx('water');
          toast(t.bottleFilled);
        },
      },
      { text: t.notNow },
    ],
  });
}
